import { useRef } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { StyledVideoPlayer } from './VideoPlayer.styled';
import useVideoPlayer from './useVideoPlayer.hook';
import PlayerControls from '../PlayerControls/PlayerControls';
import Image from 'next/image';
import { PlayCircleFilled } from '@ant-design/icons';
import Reactplayer from 'react-player';
import useCues from '../useCues';

export default function VideoPlayer({
  urls,
  posterUrl,
  onReady,
  onStart,
  onPlay,
  onSeek,
  onProgress,
  onCue,
  onCueEnd,
  onRestart,
  forceRestart,
  children,
  playing = false,
  progressInterval = 1000,
  noSeek = false,
  canPlay = true,
  cues,
  currentCue,
  showCues,
  pauseOnCue,
  seekPosition,
  showPlaybackRate = true,
  customFullscreen,
  canIncreasePlaybackRate = true,
  animatedStars,
}) {
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const handleFullScreen = useFullScreenHandle();
  const { state, actions } = useVideoPlayer({
    urls,
    videoRef,
    videoContainerRef,
    handleFullScreen,
    playing,
    onReady,
    onStart,
    onPlay,
    onProgress,
    onRestart,
    forceRestart,
    seekPosition,
  });

  useCues({
    currentTime: state.currentTime,
    progressInterval,
    playing: state.playing,
    cues,
    currentCue,
    pauseOnCue,
    onCue,
    onCueEnd,
    pause: actions.pause,
  });

  const theme = state.fullScreen ? 'inverted' : 'default';
  return (
    <StyledVideoPlayer
      ref={videoContainerRef}
      size={{
        width: state.videoWidth,
        height: state.videoHeight,
        horizontalPadding: state.horizontalPadding,
      }}
    >
      <FullScreen
        handle={handleFullScreen}
        onChange={actions.onFullScreenChange}
      >
        {animatedStars}
        <div className="player-wrapper">
          <Reactplayer
            key={state.videoKey}
            ref={videoRef}
            className="react-player"
            url={state.hlsReady ? state.url : ''}
            onDuration={actions.onDuration}
            onStart={actions.onStart}
            onSeek={onSeek}
            onProgress={actions.onProgress}
            onPause={actions.pause}
            playing={state.playing}
            controls={false}
            progressInterval={progressInterval}
            pip={false}
            config={{
              attributes: { disablePictureInPicture: true },
              hlsOptions: {
                startLevel: -1,
              },
            }}
            playsinline
            playbackRate={state.playbackRate}
            onError={(error) => {
              console.error(error);
              actions.onError();
            }}
          />

          <div className="video-overlay">
            <div
              className={`big-play${
                state.playing && !state.ended ? ' invisible' : ''
              }`}
              onClick={state.ended ? actions.play : actions.playPause}
            >
              {state.currentTime >= state.duration && (
                <div className="play-again">Play again</div>
              )}
              <PlayCircleFilled className="play-icon" />
            </div>

            {posterUrl && !state.currentTime && (
              <div className="poster-image">
                <Image
                  className="poster"
                  src={posterUrl}
                  layout="fill"
                  priority
                  alt="video-poster"
                />
              </div>
            )}
            <PlayerControls
              className="player-controls"
              duration={state.duration}
              currentTime={state.currentTime}
              bufferTime={state.bufferTime}
              ready={state.ready && canPlay}
              playing={state.playing}
              volume={state.volume}
              fullScreen={
                customFullscreen
                  ? customFullscreen.isFullscreen
                  : state.fullScreen
              }
              onPlay={actions.play}
              onPause={actions.pause}
              onSeek={noSeek ? () => {} : actions.seek}
              noSeek={noSeek}
              onFullScreen={
                customFullscreen
                  ? customFullscreen.onFullScreen
                  : actions.onFullScreen
              }
              cues={cues}
              showCues={showCues}
              currentCue={currentCue}
              theme={theme}
              videoUrls={state.urls}
              onVideoUrlChange={actions.onVideoUrlChange}
              showPlaybackRate={showPlaybackRate}
              onChangePlaybackRate={actions.changePlaybackRate}
              canIncreasePlaybackRate={canIncreasePlaybackRate}
            />
            {children}
          </div>
        </div>
      </FullScreen>
    </StyledVideoPlayer>
  );
}
